import type { IDealerFee as DealerFee, GovernmentFees, ItemizedGovernmentFee } from '@makemydeal/dr-platform-shared';
import type {
    AcceleronTaxItems,
    FeeOverride,
    GenericObject,
    Offer,
    OfferType,
    PaymentSchedule,
    paymentServicesTypes,
    Terms
} from '@makemydeal/dr-platform-types';
import type { OfferReduxBaseState } from '../../types/offerReduxBaseState';
import { CASH, LEASE, PaymentScheduleTypes, TaxClasses } from '@makemydeal/dr-platform-types';
import Config from '../../config';
import { IS_TERMID_WITH_DOWNPAYMENT } from '../../constants';
import { KEYID_DELIMETER } from '../../types/commonTypes';
import { ACQUISITION_FEE_ID, FeesCategoriesType, TermTypeWithExtension } from '../../constants/payment';
import type { DealerFeeWithOriginalCategory, ExtensionType, TermIdOptions } from '../../types/paymentTypes';
import { isExtensionValueValid } from '../../utils/termIdParser';
import { sanitizeTermId } from '../helpers';

export const getExtensionForTermId = (extension: OfferReduxBaseState = {}, getPairs: boolean = false): (number | string)[] => {
    return getPairs
        ? Object.keys(extension)
              .filter((key) => isExtensionValueValid(extension[key]))
              .map((key) => [TermTypeWithExtension[key as keyof typeof TermTypeWithExtension], extension[key]])
              .flat()
        : Object.values(extension);
};

export const getTermIdIdWithExtension = (
    offerType: OfferType | undefined,
    termMonth: number | undefined,
    extension?: number | ExtensionType,
    options: TermIdOptions = {
        getPairs: true,
        termIdWithExtention: undefined
    }
): string | undefined => {
    if (!offerType || !termMonth) {
        return;
    }

    const config = Config.getInstance();
    const isNumber = typeof extension === 'number';
    const isObject = typeof extension === 'object' && extension !== null;
    const termFields: (number | string)[] = [offerType];

    termFields.push(...getExtensionForTermId({ TERM: termMonth }, options.getPairs));

    const isTermIdWithExtention = options.termIdWithExtention ?? config.getConfig(IS_TERMID_WITH_DOWNPAYMENT);

    if (isTermIdWithExtention) {
        if (isNumber) {
            termFields.push(...getExtensionForTermId({ DOWNPAYMENT: extension }, options.getPairs));
        } else if (isObject) {
            Object.entries(extension)
                .filter(
                    ([key, value]) =>
                        key !== 'TERM' &&
                        TermTypeWithExtension[key as keyof typeof TermTypeWithExtension] &&
                        value?.toString().length
                )
                .forEach(([key, value]) => termFields.push(...getExtensionForTermId({ [key]: value }, options.getPairs)));
        }
    }
    return sanitizeTermId(termFields.join(KEYID_DELIMETER));
};

export const getTermId = (
    offerType: OfferType | undefined,
    termMonth: number | undefined,
    extension?: number | ExtensionType
): string | undefined => {
    return getTermIdIdWithExtension(offerType, termMonth, extension, { getPairs: true });
};

export const getMappedTermId = (
    offerType: OfferType,
    termMonth: number,
    extension?: number | ExtensionType
): string | undefined => {
    return getTermIdIdWithExtension(offerType, termMonth, extension, { getPairs: false });
};

export const parseOptionalNumber = (value: unknown): number | undefined => {
    if (value == null) {
        return undefined;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
            return undefined;
        }

        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
};

export const getTotalPayment = (monthlyPayment: string | number, termMonths: string | number): number => {
    return Math.round(Number(monthlyPayment)) * Math.round(Number(termMonths));
};

const isDisclosableTotal = (value: number | undefined): value is number => value !== undefined && value > 0;

export const getDisclosableTotalPayment = (
    baseMonthlyPaymentsTotal: number | undefined,
    monthlyPayment: string | number,
    termMonths: string | number
): number | undefined => {
    const authoritative = parseOptionalNumber(baseMonthlyPaymentsTotal);
    if (isDisclosableTotal(authoritative)) {
        return authoritative;
    }

    const derived = parseOptionalNumber(getTotalPayment(monthlyPayment, termMonths));
    return isDisclosableTotal(derived) ? derived : undefined;
};

export const getFlatTaxItem = (taxItems: paymentServicesTypes.TaxItems = []): paymentServicesTypes.TaxItem | undefined => {
    return taxItems?.find((taxItem) => taxItem.taxName === TaxClasses.FLAT);
};

export const getPrimaryTaxItem = (taxItems: paymentServicesTypes.TaxItems): paymentServicesTypes.TaxItem | undefined => {
    const primaryTaxes = taxItems?.filter((taxItem) => taxItem.class?.toLowerCase() === TaxClasses.PRIMARY);
    return primaryTaxes?.[0];
};

export function getTaxCapped(selectedTerm: any): boolean {
    return selectedTerm?.isTaxCapped;
}

export const getPaymentTaxCapped = (state: Offer): boolean | undefined => {
    return getTaxCapped(state.payment);
};

export const getTermMonths = (allTerms: GenericObject<number>[]): number[] => {
    return allTerms.map(({ months }) => months).sort((a, b) => a - b);
};

export const areTermsValid = (terms: Terms) => {
    return Boolean(Object.keys(terms.byId || []).length);
};

export const getDealerFeesOverrides = (dealerFees: DealerFee[] = []): FeeOverride[] => {
    // Filter and map dealer fees
    return dealerFees
        .filter(
            (fee) =>
                (fee as DealerFeeWithOriginalCategory).isManualFee === true &&
                (fee as DealerFeeWithOriginalCategory).originalCategory === FeesCategoriesType.DEALER
        )
        .map((fee: DealerFee) => ({
            feeTypeId: fee.dealerFeeTypeCode,
            feeAmount: fee.dealerFeeAmount,
            reqCapIndicator: fee.dealerFeeCapped,
            feeDescription: fee.dealerFeeDesc,
            feeName: fee.dealerFeeName,
            category: FeesCategoriesType.DEALER,
            includeInCalcIndicator: true
        }));
};

export const getLenderFeesOverrides = (dealerFees: DealerFee[] = []): FeeOverride[] => {
    return dealerFees
        .map((fee) => {
            if (
                fee.dealerFeeTypeCode === ACQUISITION_FEE_ID &&
                (fee as DealerFeeWithOriginalCategory).originalCategory === FeesCategoriesType.LENDER
            ) {
                return {
                    ...fee,
                    isManualFee: true
                };
            }
            return fee;
        })
        .filter(
            (fee) =>
                (fee as DealerFeeWithOriginalCategory).isManualFee === true &&
                (fee as DealerFeeWithOriginalCategory).originalCategory === FeesCategoriesType.LENDER
        )
        .map((fee: DealerFee) => ({
            feeTypeId: fee.dealerFeeTypeCode,
            feeAmount: fee.dealerFeeAmount,
            reqCapIndicator: fee.dealerFeeCapped,
            feeDescription: fee.dealerFeeDesc,
            feeName: fee.dealerFeeName,
            category: FeesCategoriesType.LENDER,
            includeInCalcIndicator: true
        }));
};

export const getGovernmentFeesOverrides = (governmentFees: ItemizedGovernmentFee[] = []): FeeOverride[] => {
    return governmentFees
        ?.filter((fee) => (fee as any).isManualFee === true)
        .map((fee: ItemizedGovernmentFee) => ({
            feeTypeId: fee.code,
            feeAmount: fee.amount,
            reqCapIndicator: fee.capped,
            feeDescription: fee.description,
            feeName: fee.name,
            category: FeesCategoriesType.GOVERNMENT,
            includeInCalcIndicator: true
        }));
};

export const getInitialFeesOverride = (offer: Offer): FeeOverride[] => {
    const offerType = offer.type;
    const paymentTermId = offerType === LEASE ? offer.selectedLeaseTerm : offer.selectedFinanceTerm;
    const paymentTermDetails =
        offerType === CASH ? offer[offerType] : paymentTermId ? offer.terms.byId?.[paymentTermId] : undefined;

    const dealerFees = paymentTermDetails?.dealerFees ?? ([] as DealerFee[]);
    const governmentFees = paymentTermDetails?.fees ?? ({} as GovernmentFees);

    const dealerFeesOverrides = getDealerFeesOverrides(dealerFees);
    const lenderFeesOverrides = getLenderFeesOverrides(dealerFees);
    const governmentFeesOverrides = getGovernmentFeesOverrides(governmentFees.fee?.taxFee);

    return [...lenderFeesOverrides, ...dealerFeesOverrides, ...governmentFeesOverrides];
};

export const capitalizeTaxItems = (taxItems: paymentServicesTypes.TaxItem[], flag?: boolean): AcceleronTaxItems => {
    return taxItems.map((taxItem: paymentServicesTypes.TaxItem) => ({
        ...taxItem,
        isManualTax: true,
        isCapitalize: flag
    }));
};

export const isOnePayLeaseFrequency = (paymentFrequency: PaymentSchedule | undefined): boolean => {
    return paymentFrequency !== undefined && paymentFrequency === PaymentScheduleTypes.OnePayLease;
};
